import { z } from 'zod';
import { capacityCategoryValues } from 'src/apis/monolith/mappings';
import { networkPointSuggestionSchema } from 'src/components/domain-specifics/network-points/monolith-network-point-multi-select';
import {
  descSortDirectionSchema,
  getPaginationSchema,
} from 'src/components/form/zod-schemas';
import {
  optionalDefault,
  preprocessAsArray,
} from 'src/components/form/zod-utilities';
import { tsoOptionSchema } from 'src/pages/reporting/my-transactions/tso-searchable-multi-select';
import { getPreviousGasDay } from 'src/utils/gas-day';

export function getTradesPageParamsSchema(now: number) {
  const amountSetting = 'KILO';
  const costSetting = 'SUBUNIT';
  const defaultTradingCloseFrom = getPreviousGasDay(now);
  return z.object({
    amountSetting: z.enum(['KILO', 'MEGA']).default(amountSetting),
    costSetting: z.enum(['UNIT', 'SUBUNIT']).default(costSetting),
    tradeId: z.coerce.number().nullable().default(null),
    networkPoint: z.preprocess(
      preprocessAsArray,
      z.array(networkPointSuggestionSchema).default([])
    ),
    tso: z.array(tsoOptionSchema).default([]),
    amountMin: z.coerce.number().nullable().default(null),
    amountMax: z.coerce.number().nullable().default(null),
    runtimeFrom: z.string().nullable().default(null),
    runtimeTo: z.string().nullable().default(null),
    tradingCloseFrom: z.preprocess(
      optionalDefault(defaultTradingCloseFrom),
      z.string().nullable()
    ),
    tradingCloseTo: z.string().nullable().default(null),
    procedure: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(['CFO', 'FCFS', 'OTC'])).default([])
    ),
    capacityCategory: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(capacityCategoryValues)).default([])
    ),
    direction: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(['ENTRY', 'EXIT', 'BUNDLE'])).default([])
    ),
    proposal: z
      .preprocess(
        preprocessAsArray,
        z.array(
          z.enum([
            'PROPOSAL_TO_BUY_ASSIGNMENT',
            'PROPOSAL_TO_BUY_TRANSFER_OF_USE',
            'PROPOSAL_TO_SELL_ASSIGNMENT',
            'PROPOSAL_TO_SELL_TRANSFER_OF_USE',
          ])
        )
      )
      .default([]),
    sortBy: z
      .enum([
        'ownProposal',
        'ownResponse',
        'networkPointName',
        'tsoName',
        'amountQuantityValue',
        'capacityCategory',
        'runtimeStart',
        'runtimeEnd',
        'tradingProcedure',
        'tradingClose',
        'id',
        'type',
      ])
      .default('runtimeStart'),
    sortDirection: descSortDirectionSchema,
    ...getPaginationSchema().shape,
  });
}

export function getTradeProposalsPageParamsSchema(
  now: number,
  { pageSize = 10 }: { pageSize?: number } = {}
) {
  const amountSetting = 'KILO';
  const costSetting = 'SUBUNIT';
  const defaultTradingCloseFrom = getPreviousGasDay(now);
  return z.object({
    amountSetting: z.enum(['KILO', 'MEGA']).default(amountSetting),
    costSetting: z.enum(['UNIT', 'SUBUNIT']).default(costSetting),
    proposalId: z.string().nullable().default(null),
    networkPoint: z.preprocess(
      preprocessAsArray,
      z.array(networkPointSuggestionSchema).default([])
    ),
    tso: z.array(tsoOptionSchema).default([]),
    amountMin: z.coerce.number().nullable().default(null),
    amountMax: z.coerce.number().nullable().default(null),
    runtimeFrom: z.string().nullable().default(null),
    runtimeTo: z.string().nullable().default(null),
    tradingCloseFrom: z.preprocess(
      optionalDefault(defaultTradingCloseFrom),
      z.string().nullable()
    ),
    tradingCloseTo: z.string().nullable().default(null),
    procedure: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(['CFO', 'FCFS', 'OTC'])).default([])
    ),
    capacityCategory: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(capacityCategoryValues)).default([])
    ),
    direction: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(['ENTRY', 'EXIT', 'BUNDLE'])).default([])
    ),
    sortBy: z
      .enum([
        'networkPointName',
        'tsoName',
        'amountQuantityValue',
        'capacityCategory',
        'runtimeStart',
        'priceValue',
        'tradingProcedure',
        'tradingClose',
        'proposalId',
        'type',
      ])
      .default('runtimeStart'),
    sortDirection: descSortDirectionSchema,
    ...getPaginationSchema({ pageSize }).shape,
  });
}
